import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { Router } from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import { TranslatePipe } from "@ngx-translate/core";
import {
  pathRouteDataHelper$,
  pathRouteParametersHelper$,
} from "../../../shared/helpers/path-params.helper";
import { DefaultProduct } from "../../../shared/pia-api/generated";
import { CompetitorsGroupedByBrandDefined } from "../../../shared/resolvers/competitors.resolver";

@Component({
  selector: "claas-competitor-compare-select",
  imports: [MatFormFieldModule, MatSelectModule, TranslatePipe, IconComponent],
  templateUrl: "./competitor-compare-select.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./competitor-compare-select.scss",
})
export class CompetitorCompareSelect {
  private router = inject(Router);
  data = toSignal(pathRouteDataHelper$());
  params = toSignal(pathRouteParametersHelper$());

  productDescendants = computed((): DefaultProduct[] => {
    return this.data()?.["productDescendants"] ?? [];
  });

  competitorsGroupedByBrand = computed(() => {
    return this.data()?.["competitors"]
      ? Object.values(
          this.data()["competitors"] as CompetitorsGroupedByBrandDefined,
        )
      : [];
  });

  selectedCompetitor = computed(() => {
    // use competitor id path parameter to get the selected competitor
    const competitorId = this.params()?.["competitorId"];
    const competitors = Object.values(
      this.data()["competitors"] as CompetitorsGroupedByBrandDefined,
    )
      .map((competitorWithBrand) => competitorWithBrand.items)
      .flat();

    return (
      competitors.find((competitor) => competitor.ID === competitorId) ?? null
    );
  });

  selectedCompetitorDisplay = computed(() => {
    const competitor = this.selectedCompetitor();
    if (!competitor) return null;

    const competitorWithBrand = this.competitorsGroupedByBrand().find((group) =>
      group.items.some((item) => item.ID === competitor.ID),
    );

    const brandName = competitorWithBrand?.brand ?? "";
    return `${brandName} ${competitor.Name}`;
  });

  handleCompetitorSelectChange(evt: MatSelectChange) {
    const selected = evt.value;

    if (selected) {
      const currentParams = this.params();

      // Navigate to the same route structure but with the new competitorId
      this.router.navigate([
        "/pia",
        currentParams?.["country"],
        currentParams?.["language"],
        currentParams?.["year"],
        "product",
        currentParams?.["id"],
        this.router.url.includes("arguments") ? "arguments" : "tech-data",
        currentParams?.["productId"],
        "comparison",
        selected,
      ]);
    }
  }
}
